import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { notifyUser } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { serverRequest } from "../../../util/server_request";
import { Spin } from "antd";
import { ReactComponent as LockIcon } from "../../../assets/UiElements/lock.svg";

export const OneTimePinOnboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [otp, setOTP] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(165);
  const [shake, setShake] = useState(false);

  const formatTime = useMemo(() => {
    return (timeInSeconds) => {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = timeInSeconds % 60;
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };
  }, []);

  const remainingTime = useMemo(() => {
    return seconds > 45 ? formatTime(seconds - 45) : null;
  }, [seconds, formatTime]);

  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  const toggleLoading = () => setLoading((prevState) => !prevState);

  const changeHandler = (e) => setOTP(e.target.value);

  const verifyOTP = () => {
    if (otp === "" || otp === null) {
      return notifyUser(
        "error",
        "Please enter OTP received to proceed!",
        "Validation Error"
      );
    }

    toggleLoading();

    serverRequest("POST", `verify/otp`, { otp })
      .then((result) => {
        toggleLoading();

        if (!result?.success) {
          notifyUser(
            "error",
            extractErrorMessage(result, 'Error verifying OTP. Please try again.'),
            "Error"
          );
        } else {
          notifyUser(
            "success",
            result?.message || "OTP verified successfully!",
            "Success"
          );
          navigate("/onboard/create-password", { state: { email, otp } });
        }
      })
      .catch((error) => {
        toggleLoading();
        notifyUser(
          "error",
          extractErrorMessage(error, 'Error verifying OTP. Please try again.'),
          "Error"
        );
      });
  };

  const resendOTP = () => {
    if (seconds > 45) return;

    setShake(true);
    setSeconds(165);
    setTimeout(() => {
      setShake(false);
    }, 500);
    notifyUser("success", "OTP has been resent successfully!", "Success");
  };

  const isButtonDisabled = useMemo(() => otp.length !== 6, [otp.length]);

  return (
    <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
      <div align="center">
        <p className="lead fw-bold">Enter your One Time Pin</p>
        <small className="text-center">
          We'll send a One-Time Pin(OTP) to {email?.slice(0, 6)}
          {"****"}
        </small>
        <Spin spinning={loading}>
          <div className="mt-4 w-75 mx-auto mobile-full-width">
            <omk-otp-field
              number-of-inputs="6"
              resend-text=""
              resend-button-label=""
              label="One-time Password"
              className={`mb-1 ${shake ? "shake" : ""}`}
              value={otp}
              onInput={changeHandler}
            />
            <div className="d-flex justify-content-center align-items-center mb-3">
              <p className="text-center omk-text-small text-black-100 mb-1">
                Didn't receive the OTP?&nbsp;
              </p>
              <div className="text-center">
                {remainingTime ? (
                  <p className="text-center omk-text-small text-black-50 mb-1">
                    Resend in {remainingTime}
                  </p>
                ) : (
                  <p
                    className="text-center omk-text-small mb-1"
                    style={{
                      color: "#28a745",
                      fontWeight: "bold",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={resendOTP}
                  >
                    Resend OTP
                  </p>
                )}
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <p className="text-center omk-text-small text-black-50 mb-5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LockIcon
                  style={{ marginRight: "5px", width: "16px", height: "16px", verticalAlign: "middle" }}
                />
                Your information is confidential and secure
              </p>
            </div>
            <button
              onClick={verifyOTP}
              className={`btn ${
                isButtonDisabled ? "btn-secondary" : "btn-primary"
              } mb-4 w-50 mobile-full-width`}
              disabled={isButtonDisabled}
              style={{
                borderRadius: "50px",
                padding: isButtonDisabled ? "5px 20px" : "10px 20px",
                height: "auto",
              }}
            >
              Continue
            </button>
          </div>
        </Spin>
      </div>
      <style>{shakeStyle}</style>
    </div>
  );
};

const shakeStyle = `
    @keyframes shake {
        0% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        50% { transform: translateX(5px); }
        75% { transform: translateX(-5px); }
        100% { transform: translateX(0); }
    }
    .shake {
        animation: shake 0.5s ease-out;
    }
`;

export default OneTimePinOnboard;
